import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { formGroup, formControl, FormDataType, useFormState, useFormConfig } from '@core/utils/form';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock, useCourseForm } from '@modules/course/CourseBuilder/hooks';
import { EssayDrillBlockSettings } from './EssayDrillBlockSettings';
import { CourseBuilder } from '../types';
import { BlockContainer } from '../BlockContainer';
import { Card, CardBody, Row, Col } from 'reactstrap';
import { FileService, HTMLService } from '@business/services';
import { Container } from 'reactstrap';
import { Button } from '@core/components/Button';
import { EssayDrillBlockBloc } from './EssayDrillBlockBloc';
import { useBloc } from '@core/utils/bloc';
import { tap, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { FormHTMLInline, FormInput } from '@core/components/form';
import './index.scss';
import { showYesNo } from '@modules/common';
import { FormUploadMultiple } from '@core/components/form/FormUploadMultiple';
import { UploadResponse } from '@business/entities/common';
import { R } from '@core/utils/r';
import { truncate } from 'lodash';
import { Link } from '@core/router';
import { ClientCourseInboxAPI } from '@business/api/client_course_inbox';

export enum EssayDrillBlockVariation {
  essayDrill = 'essayDrill',
}

function defineEssayDrillBlockForm(editable = false) {
  return formGroup({
    stepTitle: formControl<string>({
      placeholder: 'Essay Title',
      label: 'Essay Title',
      required: editable,
      initialValue: 'Essay Title',
    }),
    instructions: formControl<string>({
      placeholder: 'Instructions',
      label: 'Instructions',
    }),
    requireReview: formControl<boolean>({
      placeholder: 'Require Review',
      label: 'Require Review',
      initialValue: false,
      required: false,
    }),
    isSubmitted: formControl<boolean>({
      initialValue: false,
    }),
    essay: formControl<string>({
      placeholder: 'Write here',
      required: !editable,
    }),
    files: formControl<UploadResponse[]>({ label: 'Attach Files', required: false }),
    advance: defineBlockAdvanceForm({ paddingBottom: 30, paddingTop: 30 }),
  });
}

export type EssayDrillForm = ReturnType<typeof defineEssayDrillBlockForm>;
export type EssayDrillFormData = FormDataType<typeof defineEssayDrillBlockForm>;

export const EssayDrillBlock = memo((props: CourseBuilder.BlockProps<EssayDrillBlockVariation>) => {
  const { variation, preview, lessonId, editable, step } = props;
  const form = useMemo(() => defineEssayDrillBlockForm(editable), [editable]);
  const formValue = useNonNilObservable(form.value$);
  const bloc = useBloc(EssayDrillBlockBloc, lessonId);
  const [isUploading, setIsUploading] = useState(false);
  // In the student course view, once an essay is submitted there is an inbox/conversation
  // with the instructor. Resolve it by block id so we can link straight to the messages.
  const [messageInboxId, setMessageInboxId] = useState<string | null>(null);

  useEffect(() => {
    if (editable || preview || !formValue.isSubmitted || !props.id) {
      setMessageInboxId(null);
      return;
    }
    const sub = ClientCourseInboxAPI.fetchClientInboxByBlock(props.id).subscribe(
      res => setMessageInboxId(res?.id ?? null),
      () => setMessageInboxId(null),
    );
    return () => sub.unsubscribe();
  }, [editable, preview, formValue.isSubmitted, props.id]);

  useConfigureBlock({
    props,
    form,
    contentComponent: EssayDrillBlockSettings,
    contentProps: { form, variation },
  });

  const removeFile = useCallback(
    (index: number) => {
      return of(null)
        .pipe(
          switchMap(() => showYesNo('Remove Image', 'Are you sure you want to remove this image?')),
          tap(res => {
            if (res) {
              const filteredFiles = formValue.files.filter((_, i) => i !== index);
              form.patchValue({ files: filteredFiles });
            }
          }),
        )
        .subscribe();
    },
    [form, formValue.files],
  );

  const onSubmit = useCallback(() => {
    if (props.editable || props.preview || R.isEmpty(lessonId)) {
      form.patchValue({ isSubmitted: true });
      return of(null);
    } else if (formValue.isSubmitted) {
      // If already submitted, do not allow resubmission
      return of(true);
    }

    // Set submitted state immediately to prevent duplicate submissions
    form.patchValue({ isSubmitted: true });

    return bloc
      .submitEssay(lessonId || '', {
        ...formValue,
        stepId: step?.id,
        blockId: props.id,
      })
      .pipe(
        switchMap(() => of(true)),
        tap(() => {
          // Keep isSubmitted as true
        }),
      );
  }, [bloc, form, formValue, lessonId, props.editable, props.id, props.preview, step]);

  useFormConfig(form, { onSubmit });
  const { submitting } = useFormState(form, { submitting: true });

  useCourseForm(props.id, form, { onSubmit });

  return (
    <Container>
      <BlockContainer advanceForm={formValue.advance}>
        <Card className="mt-4">
          <CardBody>
            <h1 className="mb-3">{HTMLService.toReact(formValue.stepTitle)}</h1>
            <hr />
            <FormHTMLInline control={form.controls.instructions} disabled={!editable} />
            <FormInput rows={5} control={form.controls.essay} type="textarea" disabled={editable || formValue.isSubmitted} />
            <div className="dropzone-previews mb-2" id="file-previews" style={{ fontSize: 'x-small' }}>
              {formValue.files &&
                formValue.files.map((file, index) => {
                  return (
                    <MediaFileLineItem
                      key={index}
                      isRemoveable={!editable && !formValue.isSubmitted}
                      file={file}
                      removeFile={() => removeFile(index)}
                    />
                  );
                })}
            </div>
            {!editable && !formValue.isSubmitted && (
              <div className="d-flex">
                <FormUploadMultiple
                  btnLabel={'Upload Attachments'}
                  type={`multiple`}
                  control={form.controls.files}
                  autoUpload
                  autoUploadKey={'course-essay'}
                  onUploadStateChange={setIsUploading}
                />
                <div className="ml-2">
                  <Button
                    loading={submitting}
                    disabled={submitting || formValue.isSubmitted || isUploading}
                    className="mt-3 ml-2"
                    color="primary"
                    onClick={() => form.submit()}
                  >
                    {formValue.isSubmitted ? 'Submitted' : 'Submit'}
                  </Button>
                </div>
              </div>
            )}
            {!editable && !preview && formValue.isSubmitted && messageInboxId && (
              <div className="mt-3">
                <Link to={`/client/inbox/${messageInboxId}`} className="btn btn-primary btn-sm">
                  <i className="mdi mdi-message-text-outline mr-1" /> View Message
                </Link>
              </div>
            )}
          </CardBody>
        </Card>
      </BlockContainer>
    </Container>
  );
});

export interface MediaFileLineItemProps {
  file: UploadResponse;
  removeFile?(): void;
  isRemoveable?: boolean;
  canPreview?: boolean;
}

export const MediaFileLineItem = memo((props: MediaFileLineItemProps) => {
  const { file, isRemoveable = false, canPreview = false } = props;

  const onView = useCallback(() => {
    window.open(file.fileUrl, '_blank');
  }, [file.fileUrl]);

  return (
    <div className="mt-3" key={'file'}>
      <Card className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete">
        <div className="p-2">
          <Row className="align-items-center">
            <Col className="col-auto">
              {FileService.isImage(file.fileName) ? (
                <img data-dz-thumbnail="" height="80" className="avatar-sm rounded bg-light" alt={file.fileName} src={file.fileUrl} />
              ) : (
                <i className={`${FileService.getIcon(file.fileName)} font-size-24 `}></i>
              )}
            </Col>
            <Col className="">
              <div style={{ alignItems: 'center', justifyContent: 'space-between', flexDirection: 'row', display: 'flex' }}>
                <div>
                  <a className="text-muted font-weight-bold">{file.fileName}</a>
                </div>
                {!!isRemoveable && (
                  <div>
                    <i
                      onClick={() => props.removeFile && props.removeFile()}
                      className="mdi mdi-close-circle-outline icon-lg font-size-22"
                      id="Imagetooltip"
                    ></i>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </div>
      </Card>
    </div>
  );
});
